import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Box,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Campaign as CampaignIcon, Close as CloseIcon } from '@mui/icons-material';
import authInstance from '../../services/httpClient';
import Logger from '../../utils/Logger';

interface StoreMessage {
  id: string;
  messageType: string;
  title: string;
  content: string;
  status: string;
  sendPushNotification: boolean;
  createdAt: string;
  sentAt: string;
  expiresAt: string | null;
}

const ActiveGlobalMessage: React.FC = () => {
  const [activeMessage, setActiveMessage] = useState<StoreMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state for new message
  const [content, setContent] = useState('');
  const [sendPush, setSendPush] = useState(false);

  // Fetch active global message
  const fetchActiveMessage = async () => {
    try {
      setLoading(true);
      const { data } = await authInstance.get('/admin/store-messages', {
        params: {
          messageType: 'global',
          status: 'sent',
          page: 1,
          limit: 1,
        },
      });

      if (data.messages && data.messages.length > 0) {
        setActiveMessage(data.messages[0]);
      } else {
        setActiveMessage(null);
      }
    } catch (err: any) {
      Logger.error('Failed to fetch active global message:', err);
      setError(err.response?.data?.message || 'Failed to load active message');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveMessage();
  }, []);

  const handleSendMessage = async () => {
    if (!content.trim()) {
      setError('Message content is required');
      return;
    }

    if (activeMessage) {
      setError('A global message is already active. Close it first before sending a new one.');
      return;
    }

    try {
      setSending(true);
      setError('');
      setSuccess('');

      await authInstance.post('/admin/store-messages', {
        messageType: 'global',
        title: 'Global Message', // Default title (not displayed on mobile)
        content: content.trim(),
        sendPushNotification: sendPush,
      });

      // Immediately show preview with the sent data (optimistic update)
      setActiveMessage({
        id: 'temp', // Will be replaced when we fetch
        content: content.trim(),
        messageType: 'global',
        status: 'sent',
        createdAt: new Date().toISOString(),
      } as any);

      // Clear form
      setContent('');
      setSendPush(false);

      // Show success message
      setSuccess('Global message sent to all users!');

      // Fetch actual message from server to get real ID
      setTimeout(() => {
        fetchActiveMessage();
        setSuccess('');
      }, 1500);
    } catch (err: any) {
      Logger.error('Failed to send global message:', err);
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCloseMessage = async () => {
    if (!activeMessage) return;

    try {
      setClosing(true);
      setError('');

      await authInstance.post(`/admin/store-messages/${activeMessage.id}/expire`, {});

      setSuccess('Global message stopped and moved to history');
      setActiveMessage(null);

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      Logger.error('Failed to close message:', err);
      setError(err.response?.data?.message || 'Failed to close message');
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <CampaignIcon color="primary" />
            <Typography variant="h6">🌍 Global Message</Typography>
          </Box>
          {activeMessage && (
            <Alert severity="success" sx={{ py: 0 }}>
              LIVE ON ALL PHONES
            </Alert>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {activeMessage ? (
          // Show active message preview
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              This message is currently displaying on all mobile apps. Close it to stop broadcasting.
            </Alert>

            <Box
              sx={{
                p: 2,
                border: 'none',
                borderRadius: 1,
                backgroundColor: '#2196f3',
                mb: 2,
                minHeight: '46px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'semibold', flex: 1, color: 'white' }}>
                {activeMessage.content}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Sent: {new Date(activeMessage.sentAt).toLocaleString()}
              </Typography>
              {activeMessage.sendPushNotification && (
                <Typography variant="caption" color="text.secondary" display="block">
                  📱 Push notification was sent
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              color="error"
              startIcon={closing ? <CircularProgress size={20} color="inherit" /> : <CloseIcon />}
              onClick={handleCloseMessage}
              disabled={closing}
              fullWidth
            >
              {closing ? 'Closing...' : 'Stop Broadcasting & Move to History'}
            </Button>
          </Box>
        ) : (
          // Show composer form
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              No global message is currently active. Compose one below to broadcast to all users.
            </Alert>

            <TextField
              label="Message (Single line - appears as blue banner)"
              fullWidth
              value={content}
              onChange={(e) => setContent(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Enter your message here..."
              inputProps={{ maxLength: 35 }}
              helperText={`${content.length}/35 characters — keep it to 35 or it truncates in the app banner`}
            />

            <FormControlLabel
              control={<Switch checked={sendPush} onChange={(e) => setSendPush(e.target.checked)} />}
              label="📱 Also send as push notification"
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSendMessage}
              disabled={sending || !content.trim()}
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <CampaignIcon />}
            >
              {sending ? 'Broadcasting...' : 'Broadcast to All Users'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveGlobalMessage;
